import React, { useState, useEffect } from 'react';
import { Clock, ChevronRight } from 'lucide-react';
import { BakingSession } from '../../types/baking';

interface SessionCardProps {
  session: BakingSession;
  onClick: () => void;
  variant?: 'active' | 'planned';
}

export function SessionCard({ session, onClick, variant = 'active' }: SessionCardProps) {
  const [timeLeft, setTimeLeft] = useState<string>('');
  
  const calculateTimeLeft = (targetTime: Date): string => {
    const now = new Date();
    const diff = new Date(targetTime).getTime() - now.getTime();
    const overThreshold = new Date(targetTime).getTime() + 24 * 60 * 60 * 1000 - now.getTime();

    if (overThreshold <= 0) return 'Over'; // More than 24 hours have passed
    if (diff <= 0) return 'Due now';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h left`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    }
    return `${minutes}m left`;
  };
  
  // Update time left every minute
  useEffect(() => {
    // Initial calculation
    setTimeLeft(calculateTimeLeft(new Date(session.targetBakeTime)));
    
    // Set up interval to update every minute
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft(new Date(session.targetBakeTime)));
    }, 60000); // 60000 ms = 1 minute
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [session.targetBakeTime]);

  const getCurrentStage = (session: BakingSession) => {
    const now = new Date();
    const targetTime = new Date(session.targetBakeTime);
    const hoursDiff = (targetTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    switch (session.fermentationType) {
      case 'direct':
        return hoursDiff > 4 ? 'Not started' : 'Bulk fermentation';
      case 'cold-bulk':
        return hoursDiff > 16 ? 'Not started' : 'Cold fermentation';
      case 'slow-room':
        return hoursDiff > 16 ? 'Not started' : 'Room fermentation';
      case 'pre-proofed':
        return hoursDiff > 15 ? 'Not started' : 'Proofing';
      default:
        return 'In progress';
    }
  };

  const getFormattedDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 bg-[#A39995] hover:border hover:border-amber-400 hover:shadow-lg transition-colors text-left"
    >
      <div className="flex-1">
        <h3 className="font-medium text-sm text-white">{session.name}</h3>
        <div className="flex items-center gap-2 text-xs text-white mt-1">
          <span>{getFormattedDate(session.targetBakeTime)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className={`text-sm font-medium px-2 py-1 rounded-xl flex items-center gap-1 ${
          variant === 'active' 
            ? 'bg-amber-500 text-white' 
            : 'border border-white text-white'
        }`}>
          <span>{timeLeft}</span>
          <ChevronRight className="w-4 h-4 text-white" />
        </div>
      </div>
    </button>
  );
}